import { lazy } from 'react';
import type { ComponentType } from 'react';
import { Outlet } from 'react-router-dom';

type ProductionModule = typeof import('@ddc-gds/sax-hub-mod-production');
type AdminModule = typeof import('@ddc-gds/sax-hub-mod-admin');
type BusinessAgentsModule = typeof import('@ddc-gds/sax-hub-mod-business-agents');
type FinanceModule = typeof import('@ddc-gds/sax-hub-mod-finance');

function lazyNamedComponent<TModule, TName extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TName,
) {
  return lazy(async () => {
    const module = await loader();
    return { default: module[exportName] as ComponentType };
  });
}

const loadProductionModule = () => import('@ddc-gds/sax-hub-mod-production');
const loadAdminModule = () => import('@ddc-gds/sax-hub-mod-admin');
const loadBusinessAgentsModule = () => import('@ddc-gds/sax-hub-mod-business-agents');
const loadFinanceModule = () => import('@ddc-gds/sax-hub-mod-finance');

export const ProductionModuleLayout = lazy(async () => {
  const { KanbanProvider, ProductionProvider } = await loadProductionModule();

  return {
    default: function ProductionModuleLayout() {
      return (
        <ProductionProvider>
          <KanbanProvider>
            <Outlet />
          </KanbanProvider>
        </ProductionProvider>
      );
    },
  };
});

export const AdminModuleLayout = lazy(async () => {
  const { InstalledApisProvider } = await loadAdminModule();

  return {
    default: function AdminModuleLayout() {
      return (
        <InstalledApisProvider>
          <Outlet />
        </InstalledApisProvider>
      );
    },
  };
});

export const BusinessAgentsModuleLayout = lazy(async () => {
  const { BusinessAgentsProvider } = await loadBusinessAgentsModule();

  return {
    default: function BusinessAgentsModuleLayout() {
      return (
        <BusinessAgentsProvider>
          <Outlet />
        </BusinessAgentsProvider>
      );
    },
  };
});

export const FinanceModuleLayout = lazy(async () => {
  const { FinanceProvider } = await loadFinanceModule();

  return {
    default: function FinanceModuleLayout() {
      return (
        <FinanceProvider>
          <Outlet />
        </FinanceProvider>
      );
    },
  };
});

export const FinanceCashflowRoute = lazy(async () => {
  const { FinanceCashflowPage, FinanceProvider } = await loadFinanceModule();

  return {
    default: function FinanceCashflowRoute() {
      return (
        <FinanceProvider>
          <FinanceCashflowPage />
        </FinanceProvider>
      );
    },
  };
});

export const ProductionDashboardPage = lazyNamedComponent<ProductionModule, 'DashboardPage'>(
  loadProductionModule,
  'DashboardPage',
);
export const ProductionKanbanPage = lazyNamedComponent<ProductionModule, 'KanbanPage'>(
  loadProductionModule,
  'KanbanPage',
);
export const ProductionListPage = lazyNamedComponent<ProductionModule, 'ListPage'>(
  loadProductionModule,
  'ListPage',
);
export const ProductionStationsPage = lazyNamedComponent<ProductionModule, 'StationsPage'>(
  loadProductionModule,
  'StationsPage',
);
export const ProductionReportsPage = lazyNamedComponent<ProductionModule, 'ReportsPage'>(
  loadProductionModule,
  'ReportsPage',
);
export const ProductionSettingsPage = lazyNamedComponent<ProductionModule, 'SettingsPage'>(
  loadProductionModule,
  'SettingsPage',
);

export const AdminDashboardPage = lazyNamedComponent<AdminModule, 'AdminDashboardPage'>(
  loadAdminModule,
  'AdminDashboardPage',
);
export const AdminInstalledApisPage = lazyNamedComponent<AdminModule, 'AdminInstalledApisPage'>(
  loadAdminModule,
  'AdminInstalledApisPage',
);
export const AdminBusinessAgentsPage = lazyNamedComponent<AdminModule, 'AdminBusinessAgentsPage'>(
  loadAdminModule,
  'AdminBusinessAgentsPage',
);
export const AdminClientListPage = lazyNamedComponent<AdminModule, 'ClientUXList'>(
  loadAdminModule,
  'ClientUXList',
);
export const AdminClientDetailPage = lazyNamedComponent<AdminModule, 'ClientUXDetail'>(
  loadAdminModule,
  'ClientUXDetail',
);
export const AdminUserListPage = lazyNamedComponent<AdminModule, 'UserUXList'>(
  loadAdminModule,
  'UserUXList',
);
export const AdminRoleListPage = lazyNamedComponent<AdminModule, 'RoleUXList'>(
  loadAdminModule,
  'RoleUXList',
);
export const AdminPermissionListPage = lazyNamedComponent<AdminModule, 'PermissionUXList'>(
  loadAdminModule,
  'PermissionUXList',
);
export const AdminAuditLogPage = lazyNamedComponent<AdminModule, 'AdminAuditLogPage'>(
  loadAdminModule,
  'AdminAuditLogPage',
);
export const SaxAdminSecurityPage = lazyNamedComponent<AdminModule, 'SaxAdminSecurityPage'>(
  loadAdminModule,
  'SaxAdminSecurityPage',
);

export const BusinessAgentsHomePage = lazyNamedComponent<
  BusinessAgentsModule,
  'BusinessAgentsHomePage'
>(loadBusinessAgentsModule, 'BusinessAgentsHomePage');
